import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const ALL_SYMBOLS = ["SPY", "AAPL", "TSLA", "NVDA", "MSFT", "QQQ", "AMZN", "META", "GOOGL", "AMD"];

export default function ReplayConfigPage() {
  const { user } = useAuth();

  const [config, setConfig]     = useState(null);
  const [status, setStatus]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [fetching, setFetching] = useState(false);
  const [msg, setMsg]           = useState(null);

  // Draft state for the config form
  const [draft, setDraft] = useState({
    window_start_gmt:       "03:00",
    historical_offset_days: 30,
    active_symbols:         ["SPY", "AAPL", "TSLA", "NVDA", "MSFT"],
    is_enabled:             false,
  });

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get("/admin/replay/status");
      setStatus(res.data);
      const cfg = res.data.config || {};
      setConfig(cfg);
      setDraft({
        window_start_gmt:       cfg.window_start_gmt       || "03:00",
        historical_offset_days: cfg.historical_offset_days  ?? 30,
        active_symbols:         cfg.active_symbols          || ["SPY", "AAPL", "TSLA", "NVDA", "MSFT"],
        is_enabled:             !!cfg.is_enabled,
      });
    } catch {
      flash("Failed to load replay status.", false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put("/admin/replay/config", draft);
      flash("Configuration saved.");
      loadStatus();
    } catch (e) {
      flash(e?.response?.data?.error || "Save failed.", false);
    } finally {
      setSaving(false);
    }
  };

  const triggerFetch = async () => {
    setFetching(true);
    try {
      await axios.post("/admin/replay/fetch");
      flash("Data fetch started in background. Check backend logs for progress.");
    } catch (e) {
      flash(e?.response?.data?.error || "Fetch trigger failed.", false);
    } finally {
      setFetching(false);
    }
  };

  const toggleSymbol = (sym) => {
    setDraft(d => {
      const current = d.active_symbols || [];
      return {
        ...d,
        active_symbols: current.includes(sym)
          ? current.filter(s => s !== sym)
          : [...current, sym],
      };
    });
  };

  if (user?.user_type !== "superadmin") {
    return (
      <div style={page}>
        <div style={{ color: "#dc3545", fontWeight: 600 }}>Access restricted to Super Admins.</div>
      </div>
    );
  }

  // Compute window end from start
  const windowEnd = (() => {
    if (!draft.window_start_gmt) return "";
    const [h, m] = draft.window_start_gmt.split(":").map(Number);
    const total  = h * 60 + m + 390; // 6.5 hours = 390 minutes
    const endH   = Math.floor(total / 60) % 24;
    const endM   = total % 60;
    return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")} UTC`;
  })();

  const histDate = status?.historical_date || "—";
  const demoTime = status?.demo_clock_now
    ? status.demo_clock_now.replace("T", " ").slice(0, 16) + " ET"
    : "—";

  return (
    <div style={page}>
      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={pageTitle}>Market Replay</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          Configure the demo trading window and historical data replay for demo accounts.
        </p>
      </div>

      {loading ? (
        <div style={{ color: "#888", fontSize: 14 }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>

          {/* ── Left: Config form ── */}
          <div style={card}>
            <div style={cardHeader}>Configuration</div>

            {/* Enable toggle */}
            <div style={formRow}>
              <div style={formLabel}>Replay Enabled</div>
              <label style={toggleLabel}>
                <input
                  type="checkbox"
                  checked={draft.is_enabled}
                  onChange={e => setDraft(d => ({ ...d, is_enabled: e.target.checked }))}
                  style={{ marginRight: 8 }}
                />
                {draft.is_enabled
                  ? <span style={{ color: "#28a745", fontWeight: 700 }}>ON — demo accounts will cycle during the window</span>
                  : <span style={{ color: "#6c757d" }}>OFF — demo accounts are paused</span>
                }
              </label>
            </div>

            {/* Window start */}
            <div style={formRow}>
              <div style={formLabel}>Window Start (UTC)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                  type="time"
                  value={draft.window_start_gmt}
                  onChange={e => setDraft(d => ({ ...d, window_start_gmt: e.target.value }))}
                  style={timeInput}
                />
                <span style={{ fontSize: 12, color: "#888" }}>
                  → ends {windowEnd} &nbsp;(6.5 h session)
                </span>
              </div>
              <div style={hint}>
                US markets open 09:30 ET. Set this to the UTC time you want that to correspond to.
                E.g. 03:00 UTC = 09:30 ET (EDT). Weekdays only.
              </div>
            </div>

            {/* Historical offset */}
            <div style={formRow}>
              <div style={formLabel}>Historical Offset (days)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                  type="number"
                  min={1}
                  max={89}
                  value={draft.historical_offset_days}
                  onChange={e => setDraft(d => ({ ...d, historical_offset_days: Number(e.target.value) }))}
                  style={{ ...timeInput, width: 90 }}
                />
                <span style={{ fontSize: 12, color: "#888" }}>
                  Today's replay date: <strong>{histDate}</strong>
                </span>
              </div>
              <div style={hint}>
                Number of <strong>business days</strong> to look back. E.g. 1 = yesterday's trading session, 2 = two trading days ago. Weekends are skipped automatically.
              </div>
            </div>

            {/* Symbols */}
            <div style={formRow}>
              <div style={formLabel}>Active Symbols</div>
              <div style={symbolGrid}>
                {ALL_SYMBOLS.map(sym => {
                  const active = draft.active_symbols.includes(sym);
                  return (
                    <button
                      key={sym}
                      type="button"
                      onClick={() => toggleSymbol(sym)}
                      style={{
                        ...symbolBtn,
                        background:   active ? "#2c3e50" : "#f8f9fa",
                        color:        active ? "#fff"    : "#555",
                        borderColor:  active ? "#2c3e50" : "#dee2e6",
                        fontWeight:   active ? 700 : 400,
                      }}>
                      {sym}
                    </button>
                  );
                })}
              </div>
              <div style={hint}>
                Selected: {draft.active_symbols.join(", ") || "none"}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" onClick={saveConfig} disabled={saving} style={btnPrimary}>
                {saving ? "Saving…" : "Save Configuration"}
              </button>
              <button type="button" onClick={loadStatus} style={btnSecondary}>Refresh</button>
            </div>
          </div>

          {/* ── Right: Status panel ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Window status */}
            <div style={card}>
              <div style={cardHeader}>Window Status</div>
              <div style={statusGrid}>
                <StatusRow label="Replay Enabled" value={config?.is_enabled ? "Yes" : "No"}
                  color={config?.is_enabled ? "#28a745" : "#6c757d"} />
                <StatusRow label="Window Now"
                  value={status?.window_open ? "OPEN" : "CLOSED"}
                  color={status?.window_open ? "#28a745" : "#dc3545"} />
                <StatusRow label="Today's Window"
                  value={`${status?.window_start || "—"}  →  ${status?.window_end || "—"}`} />
                <StatusRow label="Historical Date" value={histDate} />
                <StatusRow label="Current Demo Time" value={demoTime} />
              </div>
            </div>

            {/* Data coverage */}
            <div style={card}>
              <div style={cardHeader}>Data Coverage</div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>
                Equity bars (5-min)
              </div>
              <div style={statusGrid}>
                <StatusRow label="Symbols" value={status?.equity_data?.symbol_count ?? "—"} />
                <StatusRow label="From"    value={formatTs(status?.equity_data?.min_date)} />
                <StatusRow label="To"      value={formatTs(status?.equity_data?.max_date)} />
              </div>

              <div style={{ fontSize: 12, color: "#666", margin: "14px 0 12px" }}>
                Options chains (daily snapshots)
              </div>
              <div style={statusGrid}>
                <StatusRow label="Symbols" value={status?.options_data?.symbol_count ?? "—"} />
                <StatusRow label="From"    value={formatTs(status?.options_data?.min_date)} />
                <StatusRow label="To"      value={formatTs(status?.options_data?.max_date)} />
              </div>
            </div>

            {/* Demo accounts */}
            {status?.demo_accounts?.length > 0 && (
              <div style={card}>
                <div style={cardHeader}>Demo Account Clocks</div>
                <table style={smallTable}>
                  <thead>
                    <tr>
                      {["Username", "Demo Clock", "Start Date"].map(h => (
                        <th key={h} style={sTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {status.demo_accounts.map(a => (
                      <tr key={a.username}>
                        <td style={sTd}><strong>{a.username}</strong></td>
                        <td style={sTd}>{a.demo_clock ? a.demo_clock.replace("T", " ").slice(0, 16) : "—"}</td>
                        <td style={sTd}>{a.demo_start_date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Fetch trigger */}
            <div style={card}>
              <div style={cardHeader}>Data Fetch</div>
              <p style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>
                The daily fetch runs automatically each weekday after 21:30 UTC (US market close).
                Use this button to trigger it manually — useful for backfilling or after changing symbols.
              </p>
              <p style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
                For initial backfill, run from the container instead:
              </p>
              <pre style={codeBlock}>
docker compose exec backend python scripts/fetch_demo_data.py \{"\n"}
    --start 2026-04-01 --end 2026-05-20
              </pre>
              <button
                type="button"
                onClick={triggerFetch}
                disabled={fetching}
                style={{ ...btnPrimary, background: "#6f42c1", marginTop: 12 }}>
                {fetching ? "Starting…" : "Fetch Today's Data"}
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}


/* ── Small components ─────────────────────────────────────────── */

function StatusRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "5px 0", borderBottom: "1px solid #f5f5f5" }}>
      <span style={{ fontSize: 12, color: "#888" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || "#2c3e50" }}>{value ?? "—"}</span>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "—";
  return String(ts).replace("T", " ").slice(0, 16);
}


/* ── Styles ──────────────────────────────────────────────────── */

const page       = { padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" };
const pageTitle  = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast      = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px",
                     borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const card       = { background: "#fff", borderRadius: 12, padding: "20px 24px",
                     boxShadow: "0 2px 10px rgba(0,0,0,0.06)" };
const cardHeader = { fontSize: 13, fontWeight: 700, color: "#2c3e50", textTransform: "uppercase",
                     letterSpacing: "0.5px", marginBottom: 16 };
const formRow    = { marginBottom: 20 };
const formLabel  = { fontSize: 11, fontWeight: 700, color: "#6c757d", textTransform: "uppercase",
                     letterSpacing: "0.5px", marginBottom: 6 };
const hint       = { fontSize: 11, color: "#aaa", marginTop: 5, lineHeight: 1.5 };
const toggleLabel = { display: "flex", alignItems: "center", fontSize: 13, cursor: "pointer" };
const timeInput  = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc",
                     fontSize: 13, width: 110 };
const symbolGrid = { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 };
const symbolBtn  = { padding: "5px 14px", borderRadius: 20, border: "1px solid", cursor: "pointer",
                     fontSize: 12, transition: "all .15s" };
const statusGrid = { display: "flex", flexDirection: "column", gap: 0 };
const btnPrimary = { padding: "9px 20px", borderRadius: 8, border: "none",
                     background: "#2c3e50", color: "#fff", cursor: "pointer",
                     fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "9px 18px", borderRadius: 8, border: "1px solid #ccc",
                       background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const smallTable = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const sTh        = { textAlign: "left", padding: "6px 8px", background: "#f8f9fa",
                     borderBottom: "2px solid #dee2e6", fontWeight: 600, fontSize: 11,
                     color: "#6c757d", textTransform: "uppercase" };
const sTd        = { padding: "7px 8px", borderBottom: "1px solid #f0f0f0" };
const codeBlock  = { background: "#f4f4f4", borderRadius: 6, padding: "10px 14px",
                     fontSize: 12, fontFamily: "monospace", whiteSpace: "pre-wrap",
                     color: "#2c3e50", margin: 0 };
